/**
 * HangarMax `BuildingConfig` → PlaneMAX area + unit parameters.
 *
 * Every HangarMax building becomes a `single_unit_community` area in Phase 1:
 *  - `box`      → faithful (walls, doors, footprint all round-trip).
 *  - `t_hangar` → approximated by its bounding box. HangarMax's `t_hangar` is a
 *    single T-shaped building, while PlaneMAX's `standard_t_hangar` template is
 *    a ROW of interlocking T units — there is no clean mapping, so the link is
 *    flagged `approximate` and the exact geometry is preserved in `raw_source`
 *    for a future `single_t_hangar` template.
 */

import type { DoorsConfig } from '@/lib/templates/single-unit-community'
import type { DoorSpec, DoorWall } from '@/lib/templates/types'
import {
  HM_WALL_THICKNESS_M,
  buildingRotationFromHmBearing,
  doorOffsetFeet,
  metersToFeet,
  wallLengthFeet,
  HM_TO_PM_DOOR_WALL,
} from './transform'
import { HM_DOOR_TYPE_TO_PM, type HmBuildingConfig, type HmDoorType } from './types'

/** Fallback colours — HangarMax ships textures, PlaneMAX only has flat colours. */
const DEFAULT_FLOOR_COLOR = '#808080'
const DEFAULT_WALL_COLOR = '#A0A0A0'

export interface MappedBuildingGeometry {
  /** Exterior footprint in feet (HangarMax dims are interior; walls sit outside). */
  widthFt: number
  depthFt: number
  heightFt: number
  rotationDeg: number
  doors: DoorsConfig
  floorColor: string
  wallColor: string
  approximate: boolean
  fidelityNotes: string[]
}

function emptyDoors(): DoorsConfig {
  return { front: [], back: [], left: [], right: [] }
}

/**
 * T-hangar fallbacks, mirroring HangarMax's own defaults when
 * `t_hangar_config` is partially filled.
 */
export function tHangarBoundingBox(building: HmBuildingConfig): {
  widthM: number
  depthM: number
} {
  const cfg = building.t_hangar_config ?? {}
  const wingWidth = cfg.wing_width_m ?? building.width_m
  const wingDepth = cfg.wing_depth_m ?? building.depth_m / 2
  const tailWidth = cfg.tail_width_m ?? 0.6 * wingWidth
  const tailDepth = cfg.tail_depth_m ?? building.depth_m / 2

  return {
    widthM: Math.max(wingWidth, tailWidth),
    depthM: wingDepth + tailDepth,
  }
}

/** Interior metres → exterior feet (HangarMax adds walls outside the footprint). */
function exteriorFeet(interiorM: number): number {
  return metersToFeet(interiorM + 2 * HM_WALL_THICKNESS_M)
}

export function mapBuildingGeometry(building: HmBuildingConfig): MappedBuildingGeometry {
  const notes: string[] = []
  const approximate = building.type === 't_hangar'

  let interiorWidthM = building.width_m
  let interiorDepthM = building.depth_m

  if (approximate) {
    const box = tHangarBoundingBox(building)
    interiorWidthM = box.widthM
    interiorDepthM = box.depthM
    notes.push(
      'T-hangar approximated by its bounding box — PlaneMAX has no single-building T template yet. Exact wing/tail geometry is preserved in the import link.'
    )
  }

  const widthFt = exteriorFeet(interiorWidthM)
  const depthFt = exteriorFeet(interiorDepthM)
  const heightFt = metersToFeet(building.height_m)

  // PlaneMAX's single-unit template uses one footprint for both the exterior and
  // the unit interior, so the imported interior ends up one wall thickness larger
  // on each side than HangarMax's.
  notes.push(
    'PlaneMAX stores one footprint per building, so the unit interior matches the HangarMax exterior (about 0.3 m larger per axis than the HangarMax interior).'
  )

  const doors = mapDoors(building, widthFt, depthFt, notes, approximate)

  if (building.wall_texture || building.floor_texture || building.door_texture) {
    notes.push('Wall/floor/door textures dropped — PlaneMAX renders flat colours.')
  }
  if (building.style || building.dimension_mode || building.wall_thickness_in != null) {
    notes.push('Building style, dimension mode and custom wall thickness dropped.')
  }
  if (building.doors_open) {
    notes.push('Open-door state dropped — PlaneMAX always renders doors closed.')
  }

  return {
    widthFt,
    depthFt,
    heightFt,
    rotationDeg: buildingRotationFromHmBearing(building.bearing ?? 0),
    doors,
    floorColor: DEFAULT_FLOOR_COLOR,
    wallColor: DEFAULT_WALL_COLOR,
    approximate,
    fidelityNotes: notes,
  }
}

function mapDoors(
  building: HmBuildingConfig,
  widthFt: number,
  depthFt: number,
  notes: string[],
  isTHangar: boolean
): DoorsConfig {
  const doors = emptyDoors()
  const source = building.doors ?? []
  if (source.length === 0) return doors

  let sawPersonnel = false
  let sawOpenDirection = false

  // HangarMax ignores `offset_m` for T-hangars and distributes doors evenly on
  // the wing wall; reproduce that rather than reading meaningless offsets.
  if (isTHangar) {
    const wingDoors = source.filter((d) => d.wall === 'front')
    const list = wingDoors.length > 0 ? wingDoors : source
    const n = list.length
    list.forEach((door, i) => {
      const widthDoorFt = metersToFeet(door.width_m)
      const slot = widthFt / n
      const offset = Math.min(
        Math.max(slot * i + (slot - widthDoorFt) / 2, 0),
        Math.max(0, widthFt - widthDoorFt)
      )
      if (door.type === 'personnel') sawPersonnel = true
      if (door.open_direction) sawOpenDirection = true
      doors.front.push({
        offset_ft: round2(offset),
        width_ft: round2(widthDoorFt),
        height_ft: round2(metersToFeet(door.height_m)),
        type: HM_DOOR_TYPE_TO_PM[(door.type ?? 'bifold') as HmDoorType],
      })
    })
    notes.push('T-hangar doors distributed evenly on the front wall, as HangarMax renders them.')
  } else {
    for (const door of source) {
      const pmWall: DoorWall = HM_TO_PM_DOOR_WALL[door.wall]
      const widthDoorFt = metersToFeet(door.width_m)
      const spec: DoorSpec = {
        offset_ft: round2(
          doorOffsetFeet({
            hmWall: door.wall,
            hmOffsetM: door.offset_m ?? 0,
            doorWidthFt: widthDoorFt,
            wallLengthFt: wallLengthFeet(pmWall, widthFt, depthFt),
          })
        ),
        width_ft: round2(widthDoorFt),
        height_ft: round2(metersToFeet(door.height_m)),
        type: HM_DOOR_TYPE_TO_PM[(door.type ?? 'bifold') as HmDoorType],
      }
      if (door.type === 'personnel') sawPersonnel = true
      if (door.open_direction) sawOpenDirection = true
      doors[pmWall].push(spec)
    }
  }

  if (sawPersonnel) {
    notes.push('Personnel doors imported as rolling doors — PlaneMAX has no personnel door type.')
  }
  if (sawOpenDirection) {
    notes.push('Door open directions dropped — PlaneMAX does not model them yet.')
  }

  return doors
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

export function buildingDisplayName(building: HmBuildingConfig, index: number): string {
  return building.name?.trim() || `HangarMax Building ${index + 1}`
}
